import React, { useState, useRef, useEffect } from "react";
import {
  setDoc,
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { Upload, Download, Clipboard } from "lucide-react";
import Papa from "papaparse";
import AuditLogger from "../../services/auditLogger";

/**
 * SalesForm component for adding and editing sales transactions
 *
 * @param {object} props - Component props
 * @param {object} props.transaction - Current transaction data
 * @param {function} props.setTransaction - Function to update transaction
 * @param {boolean} props.isEditing - Whether we're editing or creating
 * @param {string} props.editingId - ID of transaction being edited
 * @param {Array} props.products - List of available products
 * @param {function} props.onSubmitSuccess - Function to call on successful submission
 * @param {function} props.generateTransactionId - Function to generate new ID
 * @param {function} props.fetchSales - Function to refresh sales data
 */
const SalesForm = ({
  transaction,
  setTransaction,
  isEditing,
  editingId,
  products = [],
  onSubmitSuccess,
  generateTransactionId,
  fetchSales,
}) => {
  // State definitions
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [isUnknownDate, setIsUnknownDate] = useState(
    transaction.date === "unknown"
  );
  const [productVariants, setProductVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState("");
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  const fileInputRef = useRef(null);

  // Clipboard paste function
  const handlePaste = async (fieldName) => {
    try {
      const text = await navigator.clipboard.readText();
      const trimmedText = text.trim();
      
      if (trimmedText) {
        let formattedText = trimmedText;

        const isContact1Messaging =
          fieldName === "contact1" &&
          (transaction.contact1Type === "Whatsapp" ||
            transaction.contact1Type === "Telegram");
        const isContact2Messaging =
          fieldName === "contact2" &&
          (transaction.contact2Type === "Whatsapp" ||
            transaction.contact2Type === "Telegram");

        // Always normalize messaging contacts from clipboard across all views/devices.
        if (isContact1Messaging || isContact2Messaging) {
          formattedText = trimmedText.replace(/[+\uFF0B\s]/g, "");
        }
        
        setTransaction(prev => ({ ...prev, [fieldName]: formattedText }));
      }
    } catch (err) {
      console.warn('Failed to read clipboard contents:', err);
      // Fallback for browsers that don't support clipboard API
      // or when permission is denied
      setError('Unable to access clipboard. Please paste manually.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Initialize variant from transaction data when editing
  useEffect(() => {
    if (isEditing && transaction.productVariant) {
      setSelectedVariant(transaction.productVariant);
    }
  }, [isEditing, transaction.productVariant]);

  // Fetch product variants when product is Autodesk
  useEffect(() => {
    const fetchVariants = async () => {
      if (transaction.productName === "Autodesk") {
        setIsLoadingVariants(true);
        try {
          const variantsRef = collection(db, "ProductVariants");
          const q = query(variantsRef, where("productName", "==", "Autodesk"));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const variantDoc = querySnapshot.docs[0].data();
            setProductVariants(variantDoc.variants || []);

            // If editing and transaction has variant, select it
            if (isEditing && transaction.productVariant) {
              setSelectedVariant(transaction.productVariant);
            }
          } else {
            setProductVariants([]);
          }
        } catch (error) {
          console.error("Error fetching product variants:", error);
          setError("Failed to load Autodesk variants");
        } finally {
          setIsLoadingVariants(false);
        }
      } else {
        setProductVariants([]);
        setSelectedVariant("");
      }
    };

    fetchVariants();
  }, [transaction.productName, isEditing, transaction.productVariant]);

  // Fetch company options from Settings
  useEffect(() => {
    const fetchCompanyOptions = async () => {
      setIsLoadingCompanies(true);
      try {
        const settingsRef = doc(db, "Settings", "companyOptions");
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          const companies = data.companies || ["createv", "softrust", "other"];
          setCompanyOptions(companies);
        } else {
          // Fallback to default options
          setCompanyOptions(["createv", "softrust", "other"]);
        }
      } catch (error) {
        console.error("Error fetching company options:", error);
        // Fallback to default options
        setCompanyOptions(["createv", "softrust", "other"]);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompanyOptions();
  }, []);

  // Function to add to Educhecker collection
  const addToEduchecker = async (eduMail, productName) => {
    try {
      const educheckerRef = doc(db, "Educhecker", eduMail);
      await setDoc(educheckerRef, { eduMail, productName }, { merge: true });
      console.log("Added to Educhecker:", eduMail, productName);
    } catch (error) {
      console.error("Error adding to Educhecker:", error);
    }
  };

  // Function to update client data
  const updateClientData = async (saleData) => {
    const clientKey =
      saleData.contact1 || saleData.contact2 || saleData.clientEmail;
    if (!clientKey) {
      console.log(
        "Skipping client update due to missing contact info:",
        saleData
      );
      return;
    }

    try {
      const clientRef = doc(db, "Clients", clientKey);
      const clientDoc = await getDoc(clientRef);

      if (clientDoc.exists()) {
        // Update existing client
        const existingData = clientDoc.data();
        await updateDoc(clientRef, {
          clientName: saleData.clientName,
          contact1: saleData.contact1 || existingData.contact1,
          contact2: saleData.contact2 || existingData.contact2,
          clientEmail: saleData.clientEmail || existingData.clientEmail,
          products: [
            ...new Set([...existingData.products, saleData.productName]),
          ],
        });
      } else {
        // Create new client
        await setDoc(clientRef, {
          clientName: saleData.clientName,
          contact1: saleData.contact1,
          contact2: saleData.contact2,
          clientEmail: saleData.clientEmail,
          products: [saleData.productName],
        });
      }
      console.log("Client data updated successfully");
    } catch (error) {
      console.error("Error updating client data:", error);
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      let transactionId = transaction.transactionId;
      if (!transactionId) {
        transactionId = await generateTransactionId();
      }

      const dateValue = isUnknownDate
        ? "unknown"
        : new Date(transaction.date).toISOString().split("T")[0];

      let additionalData = {};
      if (transaction.productName === "Autodesk" && selectedVariant) {
        additionalData = {
          productVariant: selectedVariant,
        };
      }

      const finalTransactionData = {
        ...transaction,
        ...additionalData,
        transactionId,
        date: dateValue,
      };

      // Overwrite detection guard: check if this ID already exists before writing
      if (!isEditing) {
        const existingDoc = await getDoc(doc(db, "Sales", transactionId));
        if (existingDoc.exists()) {
          // Log the collision so we have an audit trail
          await AuditLogger.logSystemEvent('OVERWRITE_DETECTED', {
            attemptedTransactionId: transactionId,
            existingData: existingDoc.data(),
            incomingData: finalTransactionData,
            source: 'sales_form',
          });
          throw new Error(
            `ID ${transactionId} already exists in the database. The transaction was NOT saved. Please try again — a new unique ID will be generated.`
          );
        }
      }

      await setDoc(doc(db, "Sales", transactionId), finalTransactionData);
      
      // Log the sale creation
      await AuditLogger.logSalesChange(
        'CREATE', 
        transactionId, 
        {}, // no before data for new sales
        finalTransactionData,
        {
          source: 'sales_form',
          isNew: !isEditing,
          productVariant: selectedVariant || null
        }
      );
      
      await addToEduchecker(
        finalTransactionData.emailEdu,
        finalTransactionData.productName
      );
      await updateClientData(finalTransactionData);

      console.log("Transaction added/updated successfully");
      setSuccess("Transaction added/updated successfully!");

      // Reset form and states
      setSelectedVariant("");
      setIsUnknownDate(false);

      // Call success callback with the saved data for optimistic local update
      if (onSubmitSuccess) {
        onSubmitSuccess(true, finalTransactionData);
      }

      if (fetchSales) {
        fetchSales();
      }
    } catch (error) {
      console.error("Detailed error:", error);
      
      // Log validation/submission errors for audit trail
      await AuditLogger.logSystemEvent('FORM_VALIDATION_ERROR', {
        operation: 'sales_form_submission',
        error: error.message,
        formData: {
          transactionId: transaction.transactionId,
          clientName: transaction.clientName,
          productName: transaction.productName,
          operation: transaction.transactionId ? 'update' : 'create'
        },
        errorStack: error.stack
      });
      
      setError(
        `Failed to add/update transaction: ${error.message || "Unknown error"}`
      );
    }
  };

  // Handle CSV file upload
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    setCsvFile(file);
  };

  // Generate sample CSV for download
  const downloadCsvSample = () => {
    const headers = [
      "clientName",
      "company",
      "productName",
      "price",
      "amount",
      "emailEdu",
      "clientEmail",
      "paymentMethod",
      "date",
      "contact1",
      "contact1Type",
      "contact2",
      "contact2Type",
      "status",
      "notes",
      "subscriptionPeriod",
    ];
    const sampleData = [
      [
        "John Doe",
        "createv",
        "Product A",
        "100",
        "1",
        "john@sinaieng.edu.eg",
        "john@example.com",
        "N/A",
        "2023-01-01",
        "1234567890",
        "Whatsapp",
        "",
        "",
        "OnHold",
        "Sample note",
        "1 month",
      ],
    ];
    const csv = Papa.unparse([headers, ...sampleData], { encoding: "UTF-8" });
    const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "sales_sample.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Process the CSV file
  const processCsvFile = () => {
    // Implementation omitted for brevity - this would be moved from the main file
    // This function should be implemented in the main Sales.js component since it's more complex
    // and might need access to more state variables
  };

  // Toggle unknown date checkbox
  const handleUnknownDateChange = (e) => {
    const checked = e.target.checked;
    setIsUnknownDate(checked);

    if (checked) {
      // Save current date in case user toggles back
      setTransaction({
        ...transaction,
        _previousDate: transaction.date, // Store date in a temporary field
        date: "unknown",
      });
    } else {
      // Restore previous date if available, otherwise use today
      setTransaction({
        ...transaction,
        date:
          transaction._previousDate || new Date().toISOString().split("T")[0],
      });
    }
  };

  return (
    <>
      {/* Error/Success Messages */}
      {error && (
        <div style={{ color: "red", marginBottom: "10px" }}>{error}</div>
      )}
      {success && (
        <div style={{ color: "green", marginBottom: "10px" }}>{success}</div>
      )}

      <form
        onSubmit={handleSubmit}
        className="w-full p-6 bg-white rounded-lg shadow"
      >
        <h3 className="text-xl font-semibold mb-6">
          {isEditing ? "Edit Sale" : "Add New Sale"}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Client and Product Section */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Name
              </label>
              <input
                type="text"
                placeholder="Client Name"
                value={transaction.clientName}
                onChange={(e) =>
                  setTransaction({
                    ...transaction,
                    clientName: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company
              </label>
              {isLoadingCompanies ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                  Loading companies...
                </div>
              ) : (
                <select
                  value={transaction.company || "createv"}
                  onChange={(e) =>
                    setTransaction({
                      ...transaction,
                      company: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Company</option>
                  {companyOptions.map((company, index) => (
                    <option key={index} value={company}>
                      {company}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product
              </label>
              <select
                value={transaction.productName}
                onChange={(e) => {
                  const selectedProduct = products.find(
                    (p) => p.name === e.target.value
                  );
                  setTransaction({
                    ...transaction,
                    productName: e.target.value,
                    price: selectedProduct ? selectedProduct.price : "",
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.name}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Price and Amount Section */}
          <div className="space-y-4">
            {/* Autodesk Variant Selector */}
            {transaction.productName === "Autodesk" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Autodesk Variant
                </label>
                <select
                  value={selectedVariant}
                  onChange={(e) => setSelectedVariant(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isLoadingVariants}
                  required={transaction.productName === "Autodesk"}
                >
                  <option value="">Select a variant</option>
                  {productVariants.map((variant, index) => (
                    <option key={index} value={variant}>
                      {variant}
                    </option>
                  ))}
                </select>
                {isLoadingVariants && (
                  <div className="text-sm text-blue-500 mt-1">
                    Loading variants...
                  </div>
                )}
                {productVariants.length === 0 && !isLoadingVariants && (
                  <div className="text-sm text-amber-500 mt-1">
                    No variants found. Add variants in Settings.
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Price (EGP)
              </label>
              <input
                type="number"
                placeholder="Price"
                value={transaction.price}
                onChange={(e) =>
                  setTransaction({ ...transaction, price: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount
              </label>
              <input
                type="number"
                placeholder="Amount"
                value={transaction.amount}
                onChange={(e) =>
                  setTransaction({ ...transaction, amount: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact 1
              </label>
              <div className="flex gap-2">
                <div className="relative w-2/3">
                  <input
                    type="text"
                    placeholder="Contact 1"
                    value={transaction.contact1}
                    onChange={(e) =>
                      setTransaction({
                        ...transaction,
                        contact1: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => handlePaste('contact1')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-blue-500 transition-colors"
                    title="Paste from clipboard"
                  >
                    <Clipboard size={16} />
                  </button>
                </div>
                <select
                  value={transaction.contact1Type}
                  onChange={(e) =>
                    setTransaction({
                      ...transaction,
                      contact1Type: e.target.value,
                    })
                  }
                  className="w-1/3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Whatsapp">Whatsapp</option>
                  <option value="Telegram">Telegram</option>
                  <option value="Messenger">Messenger</option>
                  <option value="Email">Email</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact 2
              </label>
              <div className="flex gap-2">
                <div className="relative w-2/3">
                  <input
                    type="text"
                    placeholder="Contact 2"
                    value={transaction.contact2}
                    onChange={(e) =>
                      setTransaction({
                        ...transaction,
                        contact2: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => handlePaste('contact2')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-blue-500 transition-colors"
                    title="Paste from clipboard"
                  >
                    <Clipboard size={16} />
                  </button>
                </div>
                <select
                  value={transaction.contact2Type}
                  onChange={(e) =>
                    setTransaction({
                      ...transaction,
                      contact2Type: e.target.value,
                    })
                  }
                  className="w-1/3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Whatsapp">Whatsapp</option>
                  <option value="Telegram">Telegram</option>
                  <option value="Messenger">Messenger</option>
                  <option value="Email">Email</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Email Section */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                EDU Email
              </label>
              <div className="relative">
                <input
                  type="email"
                  placeholder="EDU Email"
                  value={transaction.emailEdu}
                  onChange={(e) =>
                    setTransaction({
                      ...transaction,
                      emailEdu: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => handlePaste('emailEdu')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-blue-500 transition-colors"
                  title="Paste from clipboard"
                >
                  <Clipboard size={16} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Email
              </label>
              <div className="relative">
                <input
                  type="email"
                  placeholder="Client Email"
                  value={transaction.clientEmail}
                  onChange={(e) =>
                    setTransaction({
                      ...transaction,
                      clientEmail: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => handlePaste('clientEmail')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-blue-500 transition-colors"
                  title="Paste from clipboard"
                >
                  <Clipboard size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Payment and Date Section */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method
              </label>
              <select
                value={transaction.paymentMethod}
                onChange={(e) =>
                  setTransaction({
                    ...transaction,
                    paymentMethod: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="instaPay">InstaPay</option>
                <option value="Vfcash">Vfcash</option>
                <option value="Binance">Binance</option>
                <option value="Gate.io">Gate.io</option>
                <option value="Paypal">Paypal</option>
                <option value="Cash">Cash</option>
                <option value="N/A">N/A</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 items-center">
                  <input
                    type="date"
                    value={isUnknownDate ? "" : transaction.date}
                    onChange={(e) =>
                      setTransaction({ ...transaction, date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isUnknownDate}
                    required={!isUnknownDate}
                  />
                </div>

                <div className="flex items-center bg-amber-50 p-2 rounded-md border border-amber-200">
                  <input
                    type="checkbox"
                    id="unknownDate"
                    checked={isUnknownDate}
                    onChange={handleUnknownDateChange}
                    className="mr-2 h-4 w-4 text-amber-600 focus:ring-amber-500"
                  />
                  <label
                    htmlFor="unknownDate"
                    className="text-sm text-amber-700 font-medium"
                  >
                    Unknown Date (check this if date is not known)
                  </label>
                </div>

                {isUnknownDate && (
                  <div className="text-xs text-gray-500 italic ml-2">
                    Note: Transactions with unknown dates will be listed at the
                    end when sorting by date.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Status and Subscription Section */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={transaction.status}
                onChange={(e) =>
                  setTransaction({ ...transaction, status: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="OnHold">OnHold</option>
                <option value="Processing">Processing</option>
                <option value="Completed">Completed</option>
                <option value="Refunded">Refunded</option>
                <option value="Expired">Expired</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subscription Period
              </label>
              <select
                value={transaction.subscriptionPeriod}
                onChange={(e) =>
                  setTransaction({
                    ...transaction,
                    subscriptionPeriod: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="1 month">1 month</option>
                <option value="3 months">3 months</option>
                <option value="6 months">6 months</option>
                <option value="12 months">12 months</option>
                <option value="24 months">24 months</option>
                <option value="36 months">36 months</option>
                <option value="one-time">One-time purchase</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notes Section - Full Width */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            placeholder="Notes"
            value={transaction.notes}
            onChange={(e) =>
              setTransaction({ ...transaction, notes: e.target.value })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
          />
        </div>

        {/* Submit Button */}
        <div className="mt-6">
          <button
            type="submit"
            className="w-full md:w-auto px-6 py-3 bg-blue-400 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            {isEditing ? "Update Transaction" : "Add Transaction"}
          </button>
        </div>
      </form>

      {/* CSV Upload Section */}
      <div className="mt-6 p-6 bg-white rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Bulk Upload</h3>
        <input
          type="file"
          accept=".csv"
          onChange={handleCsvUpload}
          className="mb-4"
          ref={fileInputRef}
        />
        <div className="flex gap-4">
          <button
            onClick={processCsvFile}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:bg-green-300 disabled:cursor-not-allowed"
            disabled={!csvFile}
          >
            <Upload size={16} />
            Upload CSV
          </button>
          <button
            onClick={downloadCsvSample}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            <Download size={16} />
            Download Sample
          </button>
        </div>
        {csvFile && (
          <div className="text-sm text-gray-500 mt-2">{csvFile.name}</div>
        )}
      </div>
    </>
  );
};

export default SalesForm;
